import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  AllTestStatuses,
  runStatuses,
  TestStatus,
  testStatusesPlusUnknown,
  TestTypeDisplay,
  testTypesDisplay,
} from '@dbt-labs/dbt-dag';
import { DropdownButton, DropdownOption } from '@dbt-labs/sourdough';

import { TestAppliedFilter } from '@/__generated__/graphql';
import { NodeStatusIconBadge } from '@/components/NodeStatusIconBadge';
import { PageHeading } from '@/components/PageHeading';
import { getNameLinkCell } from '@/components/Table/LinkCell';
import { TruncatedCell } from '@/components/TruncatedCell';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useLinkToExplorer } from '@/dbt-explorer';
import {
  TestAppliedListNode,
  useDiscoveryAppliedTests,
} from '@/hooks/discovery/appliedTests';
import { useUrlProjectParams } from '@/hooks/params';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { formatDateCommon } from '@/util/date';
import {
  translateFromTestType,
  translateToTestStatus,
  translateToTestType,
} from '@/util/resourceType';
import { toTitleCase } from '@/util/string';
import { INITIAL_PAGE_SIZE, ResourceFilterTable } from '../ResourceFilterTable';

type ExecutionInfo = TestAppliedListNode['executionInfo'];

type TableData = {
  uniqueId: string;
  name: string;
  lastKnownResult: string | null;
  executionInfo: ExecutionInfo;
  lastExecuted: string | null;
  testType: string | null;
};

const getTableData = (testNode: TestAppliedListNode): TableData | null => {
  const name = testNode.name;
  if (!name) return null;

  const lastExecuted = testNode.executionInfo?.executeCompletedAt
    ? new Date(testNode.executionInfo.executeCompletedAt)
    : null;

  return {
    uniqueId: testNode.uniqueId,
    name,
    lastKnownResult: testNode.lastKnownResult ?? null,
    executionInfo: testNode.executionInfo,
    lastExecuted: lastExecuted ? (formatDateCommon(lastExecuted).utc ?? null) : null,
    testType: translateFromTestType(testNode.testType),
  };
};

export const TestFilterView = () => {
  const pageSize = INITIAL_PAGE_SIZE;
  const { environmentId } = useDbtCloudProject();
  const { userId } = useEnvironmentScope();
  const projectScope = useUrlProjectParams();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const [searchParams, setSearchParams] = useSearchParams();

  const testStatusFilter = useMemo(
    () => (searchParams.get('testStatus') || undefined) as AllTestStatuses | undefined,
    [searchParams],
  );
  const runStatusFilter = useMemo(
    () => searchParams.get('status') as TestStatus | undefined,
    [searchParams],
  );
  const typeSearchParam = useMemo(
    () => searchParams.get('testType') as TestTypeDisplay | undefined,
    [searchParams],
  );

  const filter = useMemo((): Partial<TestAppliedFilter> => {
    return {
      lastKnownResult: translateToTestStatus(testStatusFilter),
      status: runStatusFilter,
      ...(typeSearchParam ? { testTypes: translateToTestType(typeSearchParam) } : {}),
    };
  }, [typeSearchParam, runStatusFilter, testStatusFilter]);

  const paginatedTestsQuery = useDiscoveryAppliedTests({
    environmentId,
    userId: userId as number | null,
    first: pageSize,
    filter,
    pause: userId === 'loading',
  });
  const tests = paginatedTestsQuery.data;
  const pageInfo = paginatedTestsQuery.pageInfo;

  const data = useMemo((): TableData[] | undefined => {
    return tests?.reduce((acc, test) => {
      const tableData = getTableData(test);
      if (tableData) {
        acc.push(tableData);
      }
      return acc;
    }, [] as TableData[]);
  }, [tests]);

  const testStatusFilterOptions = useMemo(
    () => [
      { label: 'All', value: 'all' },
      ...testStatusesPlusUnknown.map(
        (status): DropdownOption => ({ label: toTitleCase(status), value: status }),
      ),
    ],
    [],
  );

  const runStatusFilterOptions = useMemo(
    () => [
      { label: 'All', value: 'all' },
      ...runStatuses.map(
        (status): DropdownOption => ({ label: toTitleCase(status), value: status }),
      ),
    ],
    [],
  );

  const typeFilterOptions = useMemo(
    () => [
      { label: 'All', value: 'all' },
      ...testTypesDisplay.map(
        (type): DropdownOption => ({ label: toTitleCase(type), value: type }),
      ),
    ],
    [],
  );

  if (paginatedTestsQuery.error) return <>{paginatedTestsQuery.error.stack}</>;
  const isLoadingMore = paginatedTestsQuery.fetching;
  const isInitialLoad =
    paginatedTestsQuery.fetching && paginatedTestsQuery.data === undefined;

  return (
    <>
      <PageHeading className="mb-10">Tests</PageHeading>
      <div className="my-2 flex flex-row gap-2">
        <DropdownButton
          options={testStatusFilterOptions}
          name={'Test result'}
          listClassName="z-50"
          defaultOption={
            testStatusFilterOptions.find(
              (option) => option.value === runStatusFilter,
            ) || testStatusFilterOptions[0]
          }
          onChange={({ value }) => {
            if (typeof value !== 'string') return;
            if (value === 'all') {
              setSearchParams((p) => {
                p.delete('testStatus');
                return p;
              });
            } else {
              setSearchParams((p) => {
                p.set('testStatus', value);
                return p;
              });
            }
          }}
        />
        <DropdownButton
          options={runStatusFilterOptions}
          name={'Run status'}
          listClassName="z-50"
          defaultOption={
            runStatusFilterOptions.find((option) => option.value === runStatusFilter) ||
            runStatusFilterOptions[0]
          }
          onChange={({ value }) => {
            if (typeof value !== 'string') return;
            if (value === 'all') {
              setSearchParams((p) => {
                p.delete('status');
                return p;
              });
            } else {
              setSearchParams((p) => {
                p.set('status', value);
                return p;
              });
            }
          }}
        />
        <DropdownButton
          options={typeFilterOptions}
          name={'Test Type'}
          listClassName="z-50"
          defaultOption={
            typeFilterOptions.find((option) => option.value === typeSearchParam) ||
            typeFilterOptions[0]
          }
          testId="test-type-dropdown"
          onChange={({ value }) => {
            if (typeof value !== 'string') return;
            if (value === 'all') {
              setSearchParams((p) => {
                p.delete('testType');
                return p;
              });
            } else {
              setSearchParams((p) => {
                p.set('testType', value);
                return p;
              });
            }
          }}
        />
      </div>
      <ResourceFilterTable
        pluralizedResultName="tests"
        data={data}
        columns={[
          {
            header: 'Name',
            accessorKey: 'name',
            minSize: 150,
            cell: getNameLinkCell(getDetailsPageLink, projectScope),
          },
          {
            header: 'Test type',
            accessorKey: 'testType',
            cell: TruncatedCell,
          },
          {
            header: 'Test result',
            accessorKey: 'lastKnownResult',
            minSize: 90,
            maxSize: 110,
            cell: ({ getValue }) => (
              <NodeStatusIconBadge status={getValue() ?? 'unknown'} />
            ),
          },
          {
            header: 'Run status',
            accessorKey: 'executionInfo.lastRunStatus',
            minSize: 90,
            maxSize: 110,
            cell: ({ getValue }) => {
              const value = getValue();
              return value ? toTitleCase(value) : null;
            },
          },
          {
            header: 'Tested resource',
            accessorKey: 'name.ref',
            cell: TruncatedCell,
          },
          {
            header: 'Column',
            accessorKey: 'name.columnName',
            cell: TruncatedCell,
          },
        ]}
        pageInfo={pageInfo}
        isLoading={isInitialLoad}
        isLoadingMore={isLoadingMore}
        pageSize={pageSize}
        fetchMore={paginatedTestsQuery.fetchMore}
      />
    </>
  );
};
